
/**
 * Product Recommendations Custom Element
 * Handles loading and displaying product recommendations from Shopify
 */
// Register the custom element if not already registered
if (!window.customElements.get('product-recommendations-custom')) {
    class ProductRecommendationsCustom extends HTMLElement {
        constructor() {
            super();
            this.isLoaded = false;
        }

        connectedCallback() {
            this.loadRecommendations();
        }

        /**
         * Loads product recommendations from Shopify API
         * @private
         */
        async loadRecommendations() {
            // Prevent multiple loads
            if (this.isLoaded) {
                return;
            }

            this.isLoaded = true;

            try {
                // Get attributes with defaults
                const intent = this.getAttribute("intent") || "related";
                const productId = this.getAttribute("product");
                const limit = this.getAttribute("limit") || 4;
                const sectionId = this.extractSectionId();

                // Build API URL
                const url = `${Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;

                // Fetch recommendations with appropriate priority
                const response = await fetch(url, {
                    priority: intent === "related" ? "low" : "auto"
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Parse response and extract recommendations
                const htmlText = await response.text();
                const tempDiv = new DOMParser().parseFromString(htmlText, "text/html");
                const productRecommendationsElement = tempDiv.querySelector('product-recommendations-custom');

                // Update DOM if recommendations exist
                if (productRecommendationsElement && productRecommendationsElement.childElementCount > 0) {
                    const importedNode = document.importNode(productRecommendationsElement, true);
                    this.replaceChildren(...importedNode.childNodes);
                    this.hidden = false;
                    const scripts = this.querySelectorAll("script");
                    scripts.forEach(oldScript => {
                        const newScript = document.createElement("script");
                        [...oldScript.attributes].forEach(attr => newScript.setAttribute(attr.name, attr.value));
                        newScript.textContent = oldScript.textContent;
                        oldScript.replaceWith(newScript);
                    });
                } else {
                    // Remove element if no recommendations found
                    this.remove();
                }
            } catch (error) {
                console.error('Error loading product recommendations:', error);
                this.remove();
            }
        }

        /**
         * Extracts section ID from the current element or its parents
         * @returns {string} The section ID
         * @private
         */
        extractSectionId() {
            // Look for section ID in current element or traverse up the DOM
            let element = this;
            while (element) {
                const sectionId = element.getAttribute('data-section-id') ||
                    element.id ||
                    (element.closest('[data-section-id]')?.getAttribute('data-section-id'));

                if (sectionId) {
                    return sectionId.replace("shopify-section-", "");
                }

                element = element.parentElement;
            }

            // Fallback: generate a simple ID based on element position
            return 'product-recommendations-' + Date.now();
        }
    }

    // Helper function for extractSectionId (maintaining compatibility)
    function extractSectionId(element) {
        if (element && typeof element.extractSectionId === 'function') {
            return element.extractSectionId();
        }

        // Fallback for external calls
        let current = element;
        while (current) {
            const sectionId = current.getAttribute('data-section-id') ||
                current.id ||
                (current.closest('[data-section-id]')?.getAttribute('data-section-id'));

            if (sectionId) {
                return sectionId;
            }

            current = current.parentElement;
        }

        return 'product-recommendations-' + Date.now();
    }
    window.customElements.define('product-recommendations-custom', ProductRecommendationsCustom);
}